import {
  getLocalStorageMeasureCardLastAcknowledgedVersion,
  getLocalStorageMeasureCardMuteHistory,
} from "api/dropsLocalStorage";
import { AxiosResponse } from "axios";
import {
  AlternativeTransportPlansResponse,
  useAlternativeTransportPlans,
} from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/useAlternativeTransportPlans";
import { cleanUpMeasureOverviewLocalStorage } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/cleanUpMeasureOverviewLocalStorage";
import { CountGroup } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumnSeverityContext";
import { getSemanticsForCountGroup } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/semanticFunctions";
import { ALWAYS_MUTED_STATES } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/useMeasureCardMuteHistory";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDropsRole } from "stores/useDropsRole";
import { ROLES_WITH_ACCESS_TO_MEASURE_OVERVIEW } from "shared/types/roles";

function computeAggregatedCounts(
  plans: AlternativeTransportPlansResponse | undefined,
): CountGroup {
  const countGroup: CountGroup = {
    alarm: 0,
    warning: 0,
    transparent: 0,
  };

  if (plans) {
    Object.values(plans).forEach((planMeasures) => {
      Object.entries(planMeasures).forEach(([key, planEventInformationList]) =>
        planEventInformationList.forEach(
          ({ information: { state, uuid, version } }) => {
            if (ALWAYS_MUTED_STATES.has(state)) return;

            const acknowledgedVersion =
              getLocalStorageMeasureCardLastAcknowledgedVersion(uuid);

            if (acknowledgedVersion < version) {
              countGroup.warning += 1;
              return;
            }

            if (key === "noCoverage") {
              const muteHistory = getLocalStorageMeasureCardMuteHistory(uuid);

              if (
                muteHistory.length === 0 ||
                muteHistory[0].action === "unmuted"
              ) {
                countGroup.alarm += 1;
              }
            }
          },
        ),
      );
    });
  }

  return countGroup;
}

type MeasureOverviewDataContextValues = {
  arePlansLoading: boolean;
  plansResponse: AxiosResponse<AlternativeTransportPlansResponse> | undefined;
  globalCount: number;
  globalSemantics: ReturnType<typeof getSemanticsForCountGroup>;
  forceRefresh: () => void;
};

const MEASURE_OVERVIEW_DATA_CONTEXT_DEFAULTS: MeasureOverviewDataContextValues =
  {
    arePlansLoading: false,
    plansResponse: undefined,
    globalCount: 0,
    globalSemantics: undefined,
    forceRefresh: () => {},
  };

export const useMeasureOverviewDataContextValues =
  (): MeasureOverviewDataContextValues => {
    const { role } = useDropsRole();

    const [refreshCount, setRefreshCount] = useState(0);
    const forceRefresh = useCallback(
      () => setRefreshCount((prev) => prev + 1),
      [setRefreshCount],
    );

    const { data: plansResponse, isPending: arePlansLoading } =
      useAlternativeTransportPlans(
        ROLES_WITH_ACCESS_TO_MEASURE_OVERVIEW.has(role),
      );

    const countGroup = useMemo(
      () => computeAggregatedCounts(plansResponse?.data),
      [plansResponse?.data, refreshCount],
    );

    const globalCount = countGroup.alarm + countGroup.warning;
    const globalSemantics = getSemanticsForCountGroup(countGroup);

    useEffect(() => {
      cleanUpMeasureOverviewLocalStorage(plansResponse?.data);
    }, [plansResponse]);

    return {
      arePlansLoading,
      plansResponse,
      globalCount,
      globalSemantics,
      forceRefresh,
    };
  };

export const MeasureOverviewDataContext =
  createContext<MeasureOverviewDataContextValues>(
    MEASURE_OVERVIEW_DATA_CONTEXT_DEFAULTS,
  );
